import React, { FC } from 'react'

import styled from 'styled-components'
import { SkeletonNodeProps } from 'antd/es/skeleton/Node'
import { Skeleton } from 'antd'

import { MAX_MOBILE_WIDTH, variables } from 'src/Styled/variables'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import TikTokIcon from 'src/Assets/Svg/platform-icons/TikTok.svg?react'
import SpotifyIcon from 'src/Assets/Svg/platform-icons/SpotifyLight.svg?react'

import { Preview } from './Components/Preview'

const StyledTikTokIcon = styled(TikTokIcon)`
  width: 24px;
  height: 25px;
`

const Container = styled.div`
  width: 100%;
  display: flex;
  padding: 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  flex: 1 0 0;
  flex-basis: calc(50% - 48px);

  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    border: 1px solid ${({ theme }) => theme.color.lightGrey};
    border-radius: 16px;
    flex-basis: unset;
  }
`

const DescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  align-self: stretch;
`

const DesktopIconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    display: none;
  }
`

const MobileIconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (min-width: ${MAX_MOBILE_WIDTH}) {
    display: none;
  }
`

const Header = styled.span`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-style: normal;
  font-size: ${variables.fontSize18};
  font-weight: ${variables.fontWeight600};
  line-height: 150%;
`

const Description = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-style: normal;
  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
  line-height: 150%;

  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    display: none;
  }
`

const StyledSkeleton = styled(({ className, ...rest }: SkeletonNodeProps) => (
  <Skeleton.Input {...rest} rootClassName={className} active />
))`
  &.ant-skeleton-block {
    width: 120px;
    border-radius: 16px;
  }
  & > .ant-skeleton-input {
    min-width: 120px !important;
    width: 120px !important;
    height: 48px !important;
    border-radius: 16px !important;
    background: ${({ theme }) => theme.color.lightGrey} !important;
    @media screen and (min-width: ${MAX_MOBILE_WIDTH}) {
      width: 240px !important;
    }
  }
`

export interface TrackDetailsProps {
  platform?: CampaignPlatformTypeEnum
  coverUrl?: string
  trackName?: string
  trackAuthorName?: string
  duration?: string
  trackPreviewUrl?: string
  isTrackDataLoading?: boolean
}

const getTitle = (platform?: CampaignPlatformTypeEnum): string =>
  platform === CampaignPlatformTypeEnum.TIKTOK
    ? 'New TikTok Campaign'
    : 'New Spotify Campaign'

const getIcon = (
  platform?: CampaignPlatformTypeEnum,
): React.FunctionComponent<
  React.SVGProps<SVGSVGElement> & {
    title?: string
  }
> =>
  platform === CampaignPlatformTypeEnum.SPOTIFY ? SpotifyIcon : StyledTikTokIcon

export const TrackDetails: FC<TrackDetailsProps> = ({
  platform,
  coverUrl,
  trackName,
  trackAuthorName,
  duration,
  trackPreviewUrl,
  isTrackDataLoading,
}) => {
  const trackInfoTitle = getTitle(platform)

  if (isTrackDataLoading) {
    return <StyledSkeleton />
  }

  return (
    <Container>
      <DesktopIconWrapper>
        {React.createElement(getIcon(platform), { width: 24 })}
      </DesktopIconWrapper>

      <DescriptionWrapper>
        <HeaderWrapper>
          <MobileIconWrapper>
            {React.createElement(getIcon(platform), { width: 24 })}
          </MobileIconWrapper>

          <Header>{trackInfoTitle}</Header>
        </HeaderWrapper>

        <Description>
          Ready to amplify your music's exposure? Fill in the contact details to
          access a world of promotional opportunities!
        </Description>
      </DescriptionWrapper>
      <Preview
        coverUrl={coverUrl}
        trackName={trackName}
        trackAuthorName={trackAuthorName}
        duration={duration}
        trackPreviewUrl={trackPreviewUrl}
      />
    </Container>
  )
}
